import type { ReactElement, ReactNode } from "react";
import type { ApiClient, Unternehmen } from "@kundeninfo/api-client";
import { RollenBereich } from "./verwaltung/ansprechpartner-rollen.js";
import { DateitypenBereich } from "./verwaltung/dateitypen.js";
import { SteuersaetzeBereich } from "./verwaltung/steuersaetze.js";
import { NummernkreiseBereich } from "./verwaltung/nummernkreise.js";
import { TeamsBereich } from "./verwaltung/teams.js";
import { FaelligkeitsticketsBereich } from "./verwaltung/faelligkeitstickets.js";
import { NummerierungBereich } from "./verwaltung/nummerierung.js";
import { AccountsSection } from "./verwaltung/accounts/konten.js";
import type { AccountsClient } from "./verwaltung/accounts/konten.js";
import { CostCentresSection } from "./verwaltung/accounts/kostenstellen.js";
import type { CostCentresClient } from "./verwaltung/accounts/kostenstellen.js";
import { ContractTypesSection } from "./verwaltung/accounts/vertragsarten.js";
import type { ContractTypesClient } from "./verwaltung/accounts/vertragsarten.js";
import type { VerlassenWaechter } from "../router.js";
import type { AdressClient } from "./AdressListe.js";
import type { DatevBlockClient } from "./verwaltung/datev.js";
import type { FussbloeckeClient } from "./verwaltung/fussbloecke.js";
import type { ZahlplanModalClient } from "../components/Zahlplan.js";
import type { AnhaengeClient } from "../components/Anhaenge.js";
import "./Verwaltung.css";

/**
 * Die Bereiche der Verwaltung. Wie AKTEN_TABS in KundenAkte.tsx ist diese Liste die
 * EINE Wahrheit: Der Router liest sie (welche Adressen es gibt), das Bereichs-Untermenü in
 * der Spalte zeichnet sie (views/VerwaltungsSpalte.tsx), und der Inhaltsschalter unten
 * deckt genau sie ab.
 *
 * „Benutzer" steht bewusst vorn und ist der Rückfall bei unbekanntem Bereich: Das ist der
 * Grund, aus dem es die Verwaltung überhaupt gibt (Betriebsbefund B2 — Rolle und
 * Heimatunternehmen waren bis hierher nur per SQL setzbar).
 *
 * **`objektliste` (Etappe 2, Aufgabe 2).** Ein Bereich mit einer echten Objektliste zeigt
 * sie LINKS im Inhalt und daneben das gewählte Objekt, und die Adresse trägt es
 * (`/verwaltung/<bereich>/<id>`). Ein Bereich ohne Objektebene (`null`) steht als Ganzes
 * da — eine Tabelle oder ein Formular, das man nicht sinnvoll in Liste und Detail
 * zerschneiden kann.
 *
 * Zwischen Etappe 2 und dem Entscheid vom 06.09.2026 stand die Objektliste als zweite
 * Ebene in der Verwaltungs-Spalte. Dort war sie neben dem Bereichs-Untermenü redundant und
 * so schmal, dass Name und Marke einer Zeile umbrachen; die Spalte trägt seither wieder
 * NUR das Untermenü. Die Angabe hier bleibt trotzdem gebraucht: Sie entscheidet, ob der
 * Inhalt zweigeteilt ist, und sie trägt den Rückweg aus einem geöffneten Objekt.
 *
 * Die Angabe steht HIER und nicht im Router oder in App.tsx, weil das die eine Quelle der
 * Bereiche ist: Wer einen Bereich hinzufügt, muss die Frage beantworten, und der Typprüfer
 * lässt sie nicht aus. Der `rueckweg` daneben ist die Beschriftung des Zurück-Knopfs auf
 * dem iPhone-Einspalter — sie NENNT das Ziel (Hausregel seit Etappe 1), und deutsche
 * Artikel lassen sich aus dem Label nicht errechnen.
 *
 * **Stand Aufgabe 3: entschieden für alle zehn** (seit dem Layout-Editor, 29.09.2026: elf —
 * „Layout" hat eine Objektebene, die Unternehmen). Eine Objektebene bekommt ein Bereich
 * dann, wenn er eine echte Objektliste UND ein Einzel-Detail hat — so nennt es die
 * Spezifikation („Unternehmen, Benutzer, Artikelkatalog, Konditionen,
 * Textbausteine/Vorlagen"), und genau diese sechs haben sie. Die vier übrigen stehen rechts
 * als Ganzes, jeder aus einem eigenen Grund (nachzulesen im Kopf seiner Datei):
 *
 *  - **Ansprechpartner-Rollen** und **Datei-Kategorien** sind kurze, GEORDNETE Codelisten.
 *    Ihre Listenzeile zeigt bereits jedes Feld; ein Detail dahinter verlangte einen Klick,
 *    um dasselbe noch einmal zu sehen, und zerschnitte die Reihenfolge, die man nur im
 *    Ganzen beurteilen kann. Die Spezifikation nennt die Rollen ausdrücklich als
 *    Formular-Bereich.
 *  - **Steuersätze** pflegen ZEITREIHEN. Das Objekt ist die Serie, nicht die Zeile — „gilt
 *    heute" entsteht erst aus dem Vergleich innerhalb der Serie, und „Fortschreiben" ist
 *    eine Handlung an ihr. Eine Serie hat keine Kennung, also auch keine Adresse.
 *  - **Nummernkreise** sind eine Tabelle mit festem Umfang (je Unternehmen vier, angelegt
 *    mit dem Unternehmen). Es gibt weder Anlegen noch Löschen, für die eine Liste da wäre,
 *    und der Sinn des Bereichs ist gerade der Überblick über alle Kreise nebeneinander.
 */
export const VERWALTUNGS_BEREICHE = [
  { id: "benutzer", label: "Benutzer",
    objektliste: { rueckweg: "Zurück zur Benutzerliste" } },
  // TEAMS (Tickets Nachtrag 1e, F131): ohne Objektebene wie die Rollen — vier Felder, alle in der Zeile.
  { id: "teams", label: "Teams", objektliste: null },
  // FÄLLIGKEITSTICKETS (Terminebene 12, D19): je Unternehmen eine Zeile mit Schalter, Vorlauf, Quellen und Team — eine Tabelle
  // ohne Objektebene wie die Teams; bearbeitet wird im Panel (views/verwaltung/faelligkeitstickets.tsx).
  { id: "faelligkeitstickets", label: "Fälligkeitstickets", objektliste: null },
  { id: "unternehmen", label: "Unternehmen",
    objektliste: { rueckweg: "Zurück zur Unternehmensliste" } },
  { id: "zahlungskonditionen", label: "Zahlungskonditionen",
    objektliste: { rueckweg: "Zurück zu den Zahlungskonditionen" } },
  { id: "ansprechpartner-rollen", label: "Ansprechpartner-Rollen", objektliste: null },
  { id: "dateitypen", label: "Datei-Kategorien", objektliste: null },
  { id: "steuersaetze", label: "Steuersätze", objektliste: null },
  // KONTIERUNGSSTAMMDATEN (Lieferanten, Aufgabe 9): ohne Objektebene wie die Steuersätze — kurze Tabellen, jede Zeile
  // zeigt alle Felder; bearbeitet wird im Panel (views/verwaltung/accounts/).
  { id: "konten", label: "Konten", objektliste: null },
  { id: "kostenstellen", label: "Kostenstellen", objektliste: null },
  { id: "vertragsarten", label: "Vertragsarten", objektliste: null },
  { id: "artikel", label: "Artikelstamm",
    objektliste: { rueckweg: "Zurück zum Artikelstamm" } },
  { id: "textbausteine", label: "Textbausteine",
    objektliste: { rueckweg: "Zurück zu den Textbausteinen" } },
  { id: "angebotsvorlagen", label: "Angebotsvorlagen",
    objektliste: { rueckweg: "Zurück zu den Angebotsvorlagen" } },
  // Der LAYOUT-EDITOR (Etappe 1): je Unternehmen ein Beleg-Layout. Objektliste = die
  // Unternehmen, das Objekt ist ihr Layout.
  { id: "layout", label: "Layout",
    objektliste: { rueckweg: "Zurück zur Layoutliste" } },
  // ÜBERSCHRIFTEN-NUMMERIERUNG (Migration 048, Dirk 254): benannte Schemata, ohne Objektebene wie die Teams.
  { id: "nummerierung", label: "Nummerierung", objektliste: null },
  { id: "nummernkreise", label: "Nummernkreise", objektliste: null },
] as const;

export type VerwaltungsBereich = (typeof VERWALTUNGS_BEREICHE)[number]["id"];

/**
 * Die Objektebene eines Bereichs — `null` heisst „dieser Bereich hat keine".
 *
 * Die EINE Auskunft darüber, gelesen aus der Tabelle oben. Sie entscheidet, ob der Inhalt
 * in Liste und Detail geteilt ist, und sie trägt in App.tsx den Rückweg aus einem
 * geöffneten Objekt (`zurueckWeg`) — er NENNT die Liste, auf die er führt.
 */
export function objektlisteVon(
  bereich: VerwaltungsBereich,
): { readonly rueckweg: string } | null {
  return VERWALTUNGS_BEREICHE.find((b) => b.id === bereich)?.objektliste ?? null;
}

/**
 * Nur die Verwaltungsmethoden — so lässt sich jeder Bereich im Test mit ein paar
 * vi.fn zeichnen, ohne den ganzen ApiClient nachzubauen (Muster: KonditionenClient).
 *
 * Der Umfang ist bewusst der der GANZEN Verwaltung und nicht nur der dieser Ansicht: Er ist
 * das, was App.tsx an die Verwaltung und ihre Objekt-Bereiche reicht, und was ein
 * Prüfharnisch einmal nachbaut. Jede Bereichsdatei daneben nennt zusätzlich ihren eigenen,
 * engen Ausschnitt (`KonditionenClient`, `ArtikelstammClient` &c.) — der ist es, gegen den
 * der Bereich selbst geschrieben ist.
 *
 * Seit Aufgabe 10 (Adressen Etappe 1) kommen die fünf Adressmethoden aus `AdressClient`
 * dazu: Der Unternehmens-Bereich reicht denselben Klienten an seine Adress-Karte weiter
 * (Besitzer `companies`), und dieser Typ ist das, was App.tsx an ALLE Bereiche reicht.
 */
export type VerwaltungClient = Pick<ApiClient,
  | "listTeams" | "createTeam" | "updateTeam"
  | "listDueTicketSettings" | "updateDueTicketSetting"
  | "listNumberingSchemes" | "createNumberingScheme" | "updateNumberingScheme" | "deleteNumberingScheme"
  | "probeNumberingScheme"
  | "listUsers" | "setUserRole" | "setUserSignature" | "deleteUserSignature" |
    "userSignatureUrl" | "listUserJobTitles" | "setUserJobTitles"
  | "listCompaniesMaster" | "updateCompanyMaster" | "createCompany" | "setCompanyActive"
  | "uploadCompanyLogo" | "deleteCompanyLogo"
  | "listNumberRanges" | "updateNumberRangePattern" | "setNumberRangeStart"
  | "listPaymentTermsVerwaltung" | "createPaymentTerm" | "updatePaymentTerm"
  | "archivePaymentTerm"
  | "listContactRoles" | "createContactRole" | "updateContactRole"
  | "listFileTypes" | "createFileType" | "updateFileType" | "setFileTypeActive"
  | "listTaxRates" | "createTaxRate" | "updateTaxRate" | "setTaxRateActive"
  | "listProducts" | "createProduct" | "updateProduct" | "setProductActive"
  | "listTextBlocks" | "createTextBlock" | "updateTextBlock" | "deleteTextBlock"
  | "listOfferTemplates" | "createOfferTemplate"
  | "updateOfferTemplate" | "deleteOfferTemplate"
  // Die sechs POSITIONSWEGE samt Artikelsuche und Steuersätzen (Migration 029): Die
  // Vorlagensicht pflegt ihre Positionen mit DEMSELBEN Editor wie das Angebot
  // (components/VorlageDetail.tsx → PositionsEditor), und der spricht über den
  // Positionsbezug. `setTemplateItems` und `getOfferTemplate` sind dafür entfallen — jenes
  // war der Schreibweg des zweiten Editors, dieses holte nur dessen Positionsliste.
  | "listItems" | "createItem" | "updateItem" | "deleteItem" | "reorderItems" | "resetItem"
  | "searchProducts" | "listActiveTaxRates">
  & AdressClient
  // Der Block „DATEV" im Unternehmens-Bereich (Aufgabe 9) — dieselbe Arbeitsteilung wie
  // `AdressClient` oben.
  & DatevBlockClient
  // Der Block „Fußblöcke für Belege" (Belegdesign Fußbereich) — ebenso.
  & FussbloeckeClient
  // Der ZAHLPLAN einer Vorlage (Migration 029, Ruling V5): lesend im Detail hinter dem
  // Aufklapper, schreibend aus dem Kopf-Modal. Dieselbe Reihe, die auch Angebot und Auftrag
  // benutzen (`ZahlplanModalClient`) — nur mit dem Vorlagenbezug.
  & ZahlplanModalClient
  // Konten, Kostenstellen, Vertragsarten (Lieferanten, Aufgabe 9) — je ein enger Ausschnitt in ihrer Datei.
  & AccountsClient & CostCentresClient & ContractTypesClient
  // Der Kasten „Anlagen aus Textbausteinen" (465) einer Vorlage (Paket „Textbausteine als Anhang", 24.09.2026,
  // Aufgabe K11) — dieselbe Arbeitsteilung wie am Zahlplan: `useVerwaltungAngebotsvorlagen`
  // (views/verwaltung/angebotsvorlagen.tsx) verlangt `AnhaengeClient` in seinem
  // `AngebotsvorlagenClient`, und dieser Typ ist das, was App.tsx (dort mit dem vollen
  // `ApiClient`) UND diese Testdatei (mit ihrem eigenen Klienten-Doppel) ihm reichen.
  & AnhaengeClient;

export type VerwaltungProps = {
  client: VerwaltungClient;
  /**
   * Der offene Bereich — `null` heisst „noch keiner gewählt" (Adresse `/verwaltung`).
   *
   * Die AUSWAHL trifft seit Etappe 2, Aufgabe 1 das Untermenü in der Spalte des Rahmens
   * (views/VerwaltungsSpalte.tsx); diese Ansicht zeigt nur noch den Inhalt und braucht
   * deshalb kein `onBereich` mehr.
   */
  bereich: VerwaltungsBereich | null;
  /** Die sichtbaren Konzernunternehmen — Auswahlliste für das Heimatunternehmen. */
  companies: Unternehmen[];
  /** Das aktive Unternehmen des Fensters — die Nummernkreise zeigen nur seine (E364). */
  arbeitsunternehmenId: string | null;
  /** Der Verlassen-Wächter der Anwendung für die Bereiche, die inline stehen (Nummernkreise). */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
  /**
   * Ein OBJEKT-Bereich (Etappe 2, Aufgabe 2) — seine Liste und das Detail des gewählten
   * Objekts, nebeneinander im Rahmen `ObjektBereich` (views/verwaltung/gemeinsam.tsx).
   *
   * Es kommt von aussen, weil ein solcher Bereich als HOOK über der ganzen Anwendung lebt:
   * Sein Zustand muss den Bereichswechsel überleben, und seine Verdrahtung (Adresse,
   * Navigation, Verlassen-Wächter) gehört App.tsx — dasselbe Muster wie bei den Bereichen
   * der Etappe 1. Die Begründung im Ganzen steht an `VerwaltungBereichTeile`.
   *
   * Fehlt es, obwohl der Bereich eine Objektliste hat, steht der Inhalt leer da: ein Fehler
   * in der Verdrahtung, kein Zustand der Ansicht.
   */
  objektBereich?: ReactNode;
};

// ---------------------------------------------------------------------------
// Der Bereich selbst
// ---------------------------------------------------------------------------

/**
 * Der Verwaltungsbereich — die Oberfläche zu den Routen unter `/api/verwaltung/*`.
 *
 * Alles hier ist ADMIN-Sache. Diese Ansicht prüft das NICHT selbst: Der Server weist
 * Unberechtigte mit 403 ab (fail closed, `requireRole("ADMIN")` an jeder Route), und die
 * Oberfläche sorgt an zwei Stellen nur dafür, dass niemand versehentlich in eine sichere
 * 403 hineinläuft — der Navigationseintrag erscheint nur für ADMIN, und App.tsx leitet
 * Nicht-Administratoren von dieser Adresse auf die Kundenliste um.
 *
 * Seit Etappe 2, Aufgabe 1 zeigt sie nur noch den INHALT eines Bereichs. Die Auswahl
 * darüber stand bis dahin als Tab-Leiste (`vw__unternav`) über ebendiesem Inhalt; sie steht
 * jetzt als Untermenü in der Spalte des Rahmens (views/VerwaltungsSpalte.tsx) — zehn
 * Bereiche passen in keine Zeile, und in der Spalte ist es dieselbe Gestalt wie in allen
 * anderen Bereichen der Anwendung.
 *
 * Seit Etappe 2, Aufgabe 2 gibt es zwei Sorten Bereich. Ein Bereich MIT Objektliste
 * (`objektliste` in der Tabelle oben) wohnt in einer eigenen Datei daneben
 * (views/verwaltung/<bereich>.tsx) und füllt den Inhalt zweigeteilt: links seine Liste,
 * rechts das gewählte Objekt. Beides kommt als `objektBereich` von aussen (siehe
 * VerwaltungProps). Alle übrigen Bereiche stehen wie bisher als Ganzes hier.
 *
 * Seit Etappe 2, Aufgabe 3 wohnt AUCH JEDER dieser übrigen Bereiche in einer eigenen Datei
 * daneben (views/verwaltung/<bereich>.tsx), und sechs der zehn haben eine Objektebene: die
 * zwei Vorreiter der Aufgabe 2 plus Zahlungskonditionen, Artikelstamm, Textbausteine und
 * Angebotsvorlagen. Diese Datei ist damit Gerüst und Verdrahtung — die Bereichstabelle, der
 * Zuschnitt des Klienten und der Schalter unten, welcher Bereich gezeichnet wird. Sie kennt
 * keine Maske mehr; was sie noch selbst zeichnet, sind die VIER Bereiche ohne Objektebene.
 */
export function Verwaltung(p: VerwaltungProps): ReactElement {
  const objektliste = p.bereich === null ? null : objektlisteVon(p.bereich);
  return (
    <section className="vw" aria-label="Verwaltung">
      <h2 className="vw__titel">Verwaltung</h2>

      {p.bereich === null && (
        // Die Übersicht: Das Untermenü in der Spalte nennt die Bereiche schon, hier steht
        // deshalb nur die Ansage, dass noch nichts gewählt ist — kein zweites Menü.
        <p className="vw__hinweis">
          Bitte links einen Bereich wählen.
        </p>
      )}

      <div className="vw__inhalt">
        {/* Bereiche MIT Objektliste: Was hier steht, gehört dem Bereich in seiner eigenen
            Datei — diese Ansicht kennt ihn nicht und reicht nur seinen Platz durch. Die
            Bedingung liest die Tabelle statt die Bereiche aufzuzählen: Bekommt ein weiterer
            Bereich seine Objektliste, ist hier nichts nachzutragen (in Aufgabe 3 bekamen
            vier auf einmal eine — diese Zeile blieb unverändert). */}
        {objektliste !== null && p.objektBereich}
        {p.bereich === "teams" && <TeamsBereich client={p.client} />}
        {p.bereich === "faelligkeitstickets" && <FaelligkeitsticketsBereich client={p.client} companies={p.companies} />}
        {p.bereich === "nummerierung" && <NummerierungBereich client={p.client} />}
        {p.bereich === "ansprechpartner-rollen" && <RollenBereich client={p.client} />}
        {p.bereich === "dateitypen" && <DateitypenBereich client={p.client} />}
        {p.bereich === "steuersaetze" && <SteuersaetzeBereich client={p.client} />}
        {p.bereich === "konten" && <AccountsSection client={p.client} />}
        {p.bereich === "kostenstellen" && <CostCentresSection client={p.client} companyId={p.arbeitsunternehmenId} />}
        {p.bereich === "vertragsarten" && <ContractTypesSection client={p.client} />}
        {p.bereich === "nummernkreise" && <NummernkreiseBereich client={p.client} arbeitsunternehmenId={p.arbeitsunternehmenId}
          {...(p.registriereVerlassenWaechter === undefined ? {} : { registriereVerlassenWaechter: p.registriereVerlassenWaechter })} />}
      </div>
    </section>
  );
}
